/**
 * These hooks control UI/UX in the carriers page and are largely related to onboard, upgrade, downgrade processes for the various permutations of the BYOA feature
 */

import { useSelector } from 'react-redux';
import Global from '../../store/global-selectors';
import { useIsSellerFeatureEnabled } from '../use-is-seller-feature-enabled';
import { useNonWalletedAndNonExcludedSellerLabelProviders } from '../use-seller-label-providers';
import { BillingPlanIds, BillingPlansThatDoNotIncludeNonWalletedAccess } from '../../enums/billing-plan-ids';
import { Features } from '../../constants/features';
import { NonWalletedPlanProfileId, isPlanCodeByoa } from '../../enums/plan-profiles';
import { getIsLabelProviderIdExcludedFromYourCarriersFee } from '../../enums/providers';
import { useFeatureFlag } from '../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../feature-flags/feature-flag-declarations';
import { useIsSellerOnFeatureGatedPlan } from '../../feature-gates/use-is-seller-on-feature-gated-plan';

/**
 * Serve the old UX/UI when the new plan feature gate is live
 * @returns {boolean}
 */
export const useShouldShowNewLineupByoaUi = () => {
  return useIsSellerOnFeatureGatedPlan();
};

/**
 * Shows a speedbump for users that are adding a BYOA carrier. Is feature flag controlled and is an A/B test that last ran in Feb. 2025.
 * @deprecated
 * @todo remove this eventually
 * @returns {boolean}
 */
export const useShowNonWalletUpgradeSpeedbump = () => {
  const shouldHideInfoModal = useFeatureFlag(FeatureFlagDeclarations.enableByoAbTesting);
  const hasByoaFeature = useIsSellerFeatureEnabled(Features.NonWalletProviderAccess);

  const { planProfileId, billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  const nonWalletAndNonExcludedSellerLabelProviders = useNonWalletedAndNonExcludedSellerLabelProviders();
  const billingPlanIdParsed = parseInt(billingPlanId, 10);
  const isTrial = billingPlanIdParsed === BillingPlanIds.Trial;

  if (hasByoaFeature && !isTrial) {
    return false;
  }

  if (parseInt(planProfileId, 10) !== NonWalletedPlanProfileId) {
    return false;
  }

  if (isTrial && nonWalletAndNonExcludedSellerLabelProviders.length === 0) {
    return !shouldHideInfoModal;
  }

  if (BillingPlansThatDoNotIncludeNonWalletedAccess.includes(billingPlanIdParsed)) {
    return true;
  }

  return false;
};

/** User has access: is in a trial */
export const NON_WALLET_ELIGIBLE_TRIAL = 'NON_WALLET_ELIGIBLE_TRIAL';

/** User has access but is losing it soon: pre-downgrade */
export const NON_WALLET_ELIGIBLE_PENDING_INACTIVE = 'NON_WALLET_ELIGIBLE_PENDING_INACTIVE';

/** User does not have access: plan is insufficient */
export const NON_WALLET_ELIGIBLE_INACTIVE = 'NON_WALLET_ELIGIBLE_INACTIVE';

/**
 * User has access.
 * @todo current messaging says "for an additional monthly fee"
 */
export const NON_WALLET_ELIGIBLE_ACTIVE = 'NON_WALLET_ELIGIBLE_ACTIVE';

/** @typedef {import('./non-wallet-access-byoa-access-level').ByoaAccessLevel} ByoaAccessLevel */

/**
 * Returns a derived "access status" (ByoaAccessLevel) that's used to show a banner on the Carriers page.
 * This UI is no longer displayed for our New Plan Lineup users.
 * @deprecated
 * @todo remove this eventually
 * @returns {ByoaAccessLevel | null}
 */
export const useGetNonWalletProviderAccessStatus = () => {
  const hasByoaFeature = useIsSellerFeatureEnabled(Features.NonWalletProviderAccess);
  const { planProfileId, billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  const pendingSubscription = useSelector(Global.selectSellerPendingSubscription);
  const planProfileIdInt = parseInt(planProfileId, 10);
  const billingPlanIdInt = parseInt(billingPlanId, 10);

  if (billingPlanIdInt === BillingPlanIds.Trial) {
    return NON_WALLET_ELIGIBLE_TRIAL;
  }

  if (hasByoaFeature) {
    if (pendingSubscription?.pendingPlanCode && !isPlanCodeByoa(pendingSubscription.pendingPlanCode)) {
      return NON_WALLET_ELIGIBLE_PENDING_INACTIVE;
    }
    return NON_WALLET_ELIGIBLE_ACTIVE;
  }

  if (planProfileIdInt !== NonWalletedPlanProfileId) {
    return null;
  }

  if (BillingPlansThatDoNotIncludeNonWalletedAccess.includes(billingPlanIdInt)) {
    return NON_WALLET_ELIGIBLE_INACTIVE;
  }

  return null;
};

/**
 * This allows us to determine whether or not a carrier is "unreachable" for the user. Can be carrier-level or seller-level.
 * @deprecated
 * @todo This logic is carrier-specific and could be ported to the backend when we have time to attach a new status (Deactivated) status to the Carrier model
 * @param {string} labelProviderId
 * @returns {boolean}
 */
export const useIsCarrierRowDisabled = (labelProviderId) => {
  const nonWalletProviderAccessStatus = useGetNonWalletProviderAccessStatus();
  const hasUserLostByoaFeatureAccessOldLogic = nonWalletProviderAccessStatus === NON_WALLET_ELIGIBLE_INACTIVE;
  const isProviderSubjectToByoaFee = !getIsLabelProviderIdExcludedFromYourCarriersFee(labelProviderId);
  return hasUserLostByoaFeatureAccessOldLogic && isProviderSubjectToByoaFee;
};
